import React, { createContext, useContext, useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements as StripeElements,
  PaymentElement as StripePaymentElement,
  useElements as useStripeElements,
  useStripe as useStripeClient,
} from "@stripe/react-stripe-js";
import { isLocalStripeFrontendMockEnabled } from "../localRuntime";

const mockElements = {
  getElement() {
    return null;
  },
};

const getMockIntentAmount = (clientSecret) => {
  if (!clientSecret) {
    return 0;
  }

  const amountMatch = clientSecret.match(/amount[_-](\d+)/i);
  if (!amountMatch) {
    return 0;
  }

  return parseInt(amountMatch[1], 10);
};

const redirectTo = (url) => {
  if (!url) {
    return {
      error: { type: "validation_error", message: "Missing return URL." },
    };
  }

  window.location.assign(url);
  return {};
};

const mockStripe = {
  async retrievePaymentIntent(clientSecret) {
    return {
      paymentIntent: {
        amount: getMockIntentAmount(clientSecret),
        client_secret: clientSecret,
        status: "requires_confirmation",
      },
    };
  },
  async confirmPayment({ confirmParams }) {
    return redirectTo(confirmParams && confirmParams.return_url);
  },
  async retrieveSetupIntent(clientSecret) {
    return {
      setupIntent: {
        client_secret: clientSecret,
        status: "requires_confirmation",
      },
    };
  },
  async confirmSetup({ confirmParams }) {
    return redirectTo(confirmParams && confirmParams.return_url);
  },
};

const paymentClientPromise = isLocalStripeFrontendMockEnabled
  ? Promise.resolve(mockStripe)
  : loadStripe(process.env.REACT_APP_PAY_PORTAL_STRIPE_PK, {
      apiVersion: "2024-10-28.acacia",
    });

const MockStripeContext = createContext({
  stripe: mockStripe,
  elements: mockElements,
});

function MockPaymentElements({ children, stripe }) {
  const [value, setValue] = useState({
    stripe: mockStripe,
    elements: mockElements,
  });

  useEffect(() => {
    let isActive = true;

    Promise.resolve(stripe || mockStripe).then((resolvedStripe) => {
      if (!isActive) {
        return;
      }

      setValue({
        stripe: resolvedStripe || mockStripe,
        elements: mockElements,
      });
    });

    return () => {
      isActive = false;
    };
  }, [stripe]);

  return (
    <MockStripeContext.Provider value={value}>
      {children}
    </MockStripeContext.Provider>
  );
}

function MockPaymentElement(props) {
  return (
    <div {...props}>
      <div className="StripeElement">Mock payment element</div>
    </div>
  );
}

const useMockPaymentClient = () => useContext(MockStripeContext).stripe;
const useMockPaymentElements = () => useContext(MockStripeContext).elements;

export const PaymentElements = isLocalStripeFrontendMockEnabled
  ? MockPaymentElements
  : StripeElements;

export const PaymentElement = isLocalStripeFrontendMockEnabled
  ? MockPaymentElement
  : StripePaymentElement;

export const usePaymentClient = isLocalStripeFrontendMockEnabled
  ? useMockPaymentClient
  : useStripeClient;

export const usePaymentElements = isLocalStripeFrontendMockEnabled
  ? useMockPaymentElements
  : useStripeElements;

export { paymentClientPromise };
